/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Box, Flex, Icon, Input, Text } from '@chakra-ui/react';
import { Check, MagnifyingGlass } from 'phosphor-react';
import { Facet, normalizeText, toggleFacetValue } from './facet';

interface FacetSearchProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export const FacetSearch = ({ label, value, onChange }: FacetSearchProps) => (
  <Flex
    align="center"
    gap="8px"
    px="12px"
    py="10px"
    borderBottom="1px solid"
    borderColor="line.hairline"
  >
    <Icon as={MagnifyingGlass} w="16px" h="16px" color="content.subtle" flexShrink={0} />
    <Input
      autoFocus
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Buscar"
      aria-label={`Buscar ${label.toLowerCase()}`}
      variant="unstyled"
      h="20px"
      fontSize="13px"
      _placeholder={{ color: 'content.faint' }}
      _focusVisible={{ boxShadow: 'none' }}
    />
  </Flex>
);

interface FacetOptionRowProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

export const FacetOptionRow = ({ label, selected, onClick }: FacetOptionRowProps) => (
  <Flex
    as="button"
    type="button"
    w="full"
    h="36px"
    align="center"
    justify="space-between"
    gap="8px"
    px="12px"
    borderRadius="6px"
    cursor="pointer"
    textAlign="left"
    aria-pressed={selected}
    _hover={{ bg: 'surface.selected' }}
    onClick={onClick}
  >
    <Text fontSize="13px" color="content.strong" noOfLines={1}>
      {label}
    </Text>
    {selected && <Icon as={Check} w="14px" h="14px" color="content.strong" flexShrink={0} />}
  </Flex>
);

interface FacetOptionListProps {
  facet: Facet;
  onApply: (values: string[]) => void;
}

export const FacetOptionList = ({ facet, onApply }: FacetOptionListProps) => {
  const [search, setSearch] = useState('');
  const options = facet.onSearchChange
    ? facet.options
    : facet.options.filter((option) => normalizeText(option.label).includes(normalizeText(search)));

  const handleSearch = (value: string) => {
    setSearch(value);
    facet.onSearchChange?.(value);
  };

  return (
    <>
      {facet.searchable !== false && (
        <FacetSearch label={facet.label} value={search} onChange={handleSearch} />
      )}
      <Box maxH="264px" overflowY="auto" p="4px">
        <FacetOptionRow
          label={facet.anyLabel}
          selected={facet.values.length === 0}
          onClick={() => onApply([])}
        />
        {options.map((option) => (
          <FacetOptionRow
            key={option.value}
            label={option.label}
            selected={facet.values.includes(option.value)}
            onClick={() => onApply(toggleFacetValue(facet.values, option.value, facet.multiple))}
          />
        ))}
        {facet.loading && (
          <Text px="12px" py="8px" fontSize="13px" color="content.subtle">
            Buscando...
          </Text>
        )}
        {!facet.loading && options.length === 0 && (
          <Text px="12px" py="8px" fontSize="13px" color="content.subtle">
            Nenhum resultado.
          </Text>
        )}
      </Box>
    </>
  );
};
